import { Spin } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import styles from "./LineChartCard.module.css";

export interface BarConfig {
  key: string;
  label: string;
  color: string;
}

export interface BarChartCardProps {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  bars: BarConfig[];
  yLabel?: string;
  loading?: boolean;
  error?: string | null;
  headerExtra?: React.ReactNode;
  showValueLabels?: boolean;
  valueLabelSuffix?: string;
  valueLabelFontSize?: number;
  valueLabelOffset?: number;
}

const formatBarValueLabel = (value: unknown, suffix: string) => {
  if (typeof value !== "number") return "";

  const formattedValue = Number.isInteger(value)
    ? value.toString()
    : value.toFixed(1).replace(/\.0$/, "");

  return `${formattedValue}${suffix}`;
};

const BarChartCard = ({
  title,
  data,
  xKey,
  bars,
  yLabel,
  loading = false,
  error = null,
  headerExtra,
  showValueLabels = false,
  valueLabelSuffix = "",
  valueLabelFontSize = 8,
  valueLabelOffset = 2,
}: BarChartCardProps) => {
  return (
    <div className={styles.card} data-chart-card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <h2 className={styles.title} style={{ margin: 0 }}>{title}</h2>
        {headerExtra}
      </div>

      <div className={styles.chartArea}>
        {loading && (
          <div className={styles.centered}>
            <Spin size="small" /> Carregant dades…
          </div>
        )}
        {!loading && error && (
          <div className={styles.centered}>
            <p className={styles.errorMessage}>{error}</p>
          </div>
        )}
        {!loading && !error && data.length === 0 && (
          <div className={styles.centered}>
            <p className={styles.emptyMessage}>Sense dades disponibles</p>
          </div>
        )}
        {!loading && !error && data.length > 0 && (
          <ResponsiveContainer width="100%" height="100%" minHeight={0}>
            <BarChart
              data={data}
              margin={{ top: showValueLabels ? 28 : 8, right: 24, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF3" vertical={false} />
              <XAxis
                dataKey={xKey}
                tick={{ fontSize: 12, fill: "#717C8B" }}
                tickLine={false}
                axisLine={{ stroke: "#E8EDF3" }}
              />
              <YAxis
                label={
                  yLabel
                    ? {
                        value: yLabel,
                        angle: -90,
                        position: "insideLeft",
                        style: { fontSize: 12, fill: "#717C8B" },
                        offset: 8,
                      }
                    : undefined
                }
                tick={{ fontSize: 12, fill: "#717C8B" }}
                tickLine={false}
                axisLine={false}
                width={60}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #E8EDF3",
                  fontSize: 13,
                }}
                cursor={{ fill: "#F8FAFC" }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
              />
              {bars.map(({ key, label, color }) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={label}
                  fill={color}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={40}
                >
                  {showValueLabels && (
                    <LabelList
                      dataKey={key}
                      position="top"
                      offset={valueLabelOffset}
                      formatter={(value: unknown) => formatBarValueLabel(value, valueLabelSuffix)}
                      style={{
                        fill: "#252B33",
                        fontSize: valueLabelFontSize,
                        fontWeight: 400,
                      }}
                    />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default BarChartCard;
